// Phase 4 OM — Packing slips section on supply WorkOrderDetail.
//
// Volkan ratify 2026-06-03 + memory feedback_packing_slip_content_segregation_dual_pdf.
// Renders the persona-filtered slip set: producer sees minimal, Aybars NJ
// sees full. Direct-dropship producer is the sole exception (full slip
// because they ship directly to the customer).
//
// S8 invariant: content summaries are platform-neutral; no "seller" / "etsy"
// wording in any visible string.

import { useQuery } from '@tanstack/react-query'
import { FileText, ShieldCheck } from 'lucide-react'
import { apiUrl } from '../../lib/api'
import { toast } from '../../lib/toast'
import { useT } from '../../i18n'
import { workOrderPackingSlipsI18n } from '../../i18n/supply/work-order-packing-slips'
import type { PackingSlipMetadata } from '../../mocks/data/packing-slips'

type Props = { woId: string; persona: string }

export function WorkOrderPackingSlips({ woId, persona }: Props) {
  const t = useT(workOrderPackingSlipsI18n)
  const { data, isLoading, isError } = useQuery<{ data: PackingSlipMetadata[] }>({
    queryKey: ['supply', 'work-orders', persona, woId, 'packing-slips'],
    queryFn: async () => {
      const res = await fetch(
        apiUrl(`/supply/my-work-orders/${woId}/packing-slips?persona=${persona}`),
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })

  const slips = data?.data ?? []
  if (!isLoading && !isError && slips.length === 0) return null

  async function handleDownload(slip: PackingSlipMetadata) {
    // Real-mode: pdf_url is the backend stream endpoint (bearer added by the
    // window.fetch auth patch). Mock-mode: pdf_url is a non-existent /m0 asset,
    // so the fetch 404s / is not a PDF → fall through to the simulation toast.
    try {
      const res = await fetch(slip.pdf_url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      if (!blob.type.includes('pdf')) throw new Error('not-pdf')
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${slip.id}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast.info(t.downloadMockTitle, {
        description: t.downloadMockDesc,
        audience: 'supply',
      })
    }
  }

  return (
    <section className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <header className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--color-text)]">
          {t.title(slips.length)}
        </h3>
        <span className="text-xs text-[var(--color-text-muted)]">{t.subtitle}</span>
      </header>

      {isLoading && <div className="mt-3 text-sm text-[var(--color-text-muted)]">{t.loading}</div>}
      {isError && <div className="mt-3 text-sm text-[var(--color-danger)]">{t.errorTitle}</div>}

      {slips.length > 0 && (
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {slips.map((slip) => (
            <article
              key={slip.id}
              className={
                'rounded-md border p-3 ' +
                (slip.variant === 'mehmet_minimal'
                  ? 'border-[var(--color-border)] bg-[var(--color-surface-muted)]'
                  : 'border-[var(--color-warning)] bg-[var(--color-surface-muted)]')
              }
            >
              <header className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[var(--color-text-muted)]" />
                  <div>
                    <div className="text-sm font-medium text-[var(--color-text)]">
                      {t.variantLabels[slip.variant]}
                    </div>
                    <div className="text-[10px] font-mono text-[var(--color-text-muted)]">
                      {slip.id}
                    </div>
                  </div>
                </div>
                {slip.contains_customer_pii && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-surface)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--color-warning)] ring-1 ring-[var(--color-warning)]">
                    <ShieldCheck className="h-3 w-3" />
                    {t.containsPii}
                  </span>
                )}
              </header>
              <ul className="mt-2 space-y-0.5 text-xs text-[var(--color-text)]">
                {slip.content_summary.map((line, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="mt-1 inline-block h-1 w-1 shrink-0 rounded-full bg-[var(--color-text-muted)]" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex items-center justify-between gap-2">
                <span className="text-[10px] text-[var(--color-text-muted)]">
                  {t.generatedAt(new Date(slip.generated_at).toLocaleString())}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    void handleDownload(slip)
                  }}
                  className="text-xs font-medium text-[var(--order-link-fg)] hover:underline"
                >
                  {t.downloadButton}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <p className="mt-3 text-[10px] text-[var(--color-text-muted)]">{t.v15Note}</p>
    </section>
  )
}
